import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import axiosInstance from 'src/utils/axios';
import {
  setLoading,
  setLoadingProgress,
  handleIPEResponse,
  setError,
  setAnswerInFlight,
  setAnswerError,
  setAnswerGroupId,
} from './interviewSlice';
import type { InterviewState } from './types';

// ─── Compact debug formatters ─────────────────────────────────────────────

const _a = (
  arr: unknown[] | null | undefined,
  showValues?: boolean
): string => {
  if (arr == null) {
    return '-';
  }
  const len = arr.length;
  if (showValues && len <= 8) {
    return `[${len} ${arr.join(',')}]`;
  }
  return `[${len}]`;
};

const _ag = (ag: Record<number, number[]> | null | undefined): string => {
  if (ag == null) {
    return '-';
  }
  const keys = Object.keys(ag);
  if (keys.length === 0) {
    return '{}';
  }
  return '{' + keys.map((k) => `${k}:${_a(ag[Number(k)])}`).join(', ') + '}';
};

// ─── Extracted API functions (testable without React context) ─────────────────

export interface IPEParams {
  surveyId: number;
  onBehalfOf?: number | null;
  skippedQitIds?: number[];
  onlyQitIds?: number[];
  answerGroupQits?: Record<number, number[]> | null;
  deferred?: Record<number, number[]>;
}

export interface AnswerPayload {
  surveyId: number;
  qitId: number;
  answerGroupId?: number | null;
  text?: string;
  textArea?: string;
  questionChoices?: number[];
  file?: File;
}

function buildAnswerBody(
  payload: AnswerPayload,
  onBehalfOf?: number | null
): FormData | Record<string, unknown> {
  // Use FormData only for file uploads; JSON for everything else
  if (payload.file) {
    const formData = new FormData();
    formData.append('qit_id', String(payload.qitId));
    if (payload.answerGroupId) {
      formData.append('answer_group_id', String(payload.answerGroupId));
    }
    if (onBehalfOf) {
      formData.append('on_behalf_of', String(onBehalfOf));
    }
    formData.append('file', payload.file);
    return formData;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body: Record<string, any> = { qit_id: payload.qitId };
  if (payload.answerGroupId) {
    body.answer_group_id = payload.answerGroupId;
  }
  if (onBehalfOf) {
    body.on_behalf_of = onBehalfOf;
  }
  if (payload.text !== undefined) {
    body.text = payload.text;
  }
  if (payload.textArea !== undefined) {
    body.text_area = payload.textArea;
  }
  if (payload.questionChoices) {
    body.question_choices = payload.questionChoices;
  }
  return body;
}

export async function callIPEApi(params: IPEParams): Promise<unknown> {
  const body: Record<string, unknown> = {
    survey_id: params.surveyId,
  };
  if (params.onBehalfOf) {
    body.member_id = params.onBehalfOf;
  }
  if (params.deferred && Object.keys(params.deferred).length > 0) {
    body.deferred = params.deferred;
  }
  if (
    params.answerGroupQits &&
    Object.keys(params.answerGroupQits).length > 0
  ) {
    body.answer_group_qits = params.answerGroupQits;
  }
  if (params.onlyQitIds?.length) {
    const agSet = new Set(Object.values(params.answerGroupQits ?? {}).flat());
    const bare = params.onlyQitIds.filter((id) => !agSet.has(id));
    if (bare.length > 0) {
      body.only_qit_ids = bare;
    }
  }
  if (process.env.NODE_ENV !== 'production') {
    const onlySent = body.only_qit_ids as number[] | undefined;
    const agSent = body.answer_group_qits as
      | Record<number, number[]>
      | undefined;
    console.log(
      `[IPE>>] survey=${params.surveyId}  ag_qits=${_ag(agSent)}  only=${_a(
        onlySent,
        true
      )}  skipped=${_a(params.skippedQitIds)}`
    );
  }
  const response = await axiosInstance.post('/v2/interview/next/', body);
  if (process.env.NODE_ENV !== 'production') {
    console.log(
      `[IPE<<BODY] survey=${params.surveyId}`,
      JSON.stringify(response.data, null, 2)
    );
  }
  return response.data;
}

export async function submitAnswerApi(
  payload: AnswerPayload,
  onBehalfOf?: number | null
): Promise<unknown> {
  const body = buildAnswerBody(payload, onBehalfOf);
  const response = await axiosInstance.post(
    `/v2/questionnaire/survey/${payload.surveyId}/answer/`,
    body
  );
  return response.data;
}

export async function validateVisibilityApi(
  surveyId: number,
  qitIds: number[],
  onBehalfOf?: number | null
): Promise<Record<number, boolean>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body: Record<string, any> = {
    survey_id: surveyId,
    qit_ids: qitIds,
  };
  if (onBehalfOf) {
    body.on_behalf_of = onBehalfOf;
  }

  const response = await axiosInstance.post<{
    results: Record<string, boolean>;
  }>('/v2/interview/validate-visibility/', body);

  const results: Record<string, boolean> = response.data.results;
  const parsed: Record<number, boolean> = {};
  for (const [key, value] of Object.entries(results)) {
    parsed[Number(key)] = value;
  }
  return parsed;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

interface CallIPEParams {
  skippedQitIds?: number[];
  onlyQitIds?: number[];
  answerGroupQits?: Record<number, number[]> | null;
  deferred?: Record<number, number[]>;
  surveyIdOverride?: number;
  /**
   * Pass the on-behalf-of User.id directly from a URL param or context value
   * at the call site. Use this on mount-time calls (e.g. InterviewRoute,
   * DataVaultInterviewRoute) where `dispatch(initInterview)` and `callIPE`
   * run in the same effect — without this override the `onBehalfOf` captured
   * in the callIPE closure is still the stale pre-dispatch value (null).
   *
   * When provided (including null), this value takes precedence over the
   * Redux `state.interview.onBehalfOf`. When omitted, the Redux value is used
   * (correct for all calls made after the component has re-rendered).
   */
  onBehalfOfOverride?: number | null;
}

interface UseIPEReturn {
  callIPE: (params?: CallIPEParams) => Promise<void>;
  loading: boolean;
}

export function useIPE(): UseIPEReturn {
  const dispatch = useDispatch();
  const store = useStore<{ interview: InterviewState }>();
  const { surveyId, onBehalfOf, loading } = useSelector(
    (state: { interview: InterviewState }) => state.interview
  );

  const callIPE = async (params?: CallIPEParams): Promise<void> => {
    const effectiveSurveyId = params?.surveyIdOverride || surveyId;
    if (!effectiveSurveyId) {
      return;
    }
    // Use the explicit override when provided (even null = self-mode) so that
    // mount-time calls in route components bypass the stale Redux closure.
    // Fall back to the Redux value for all in-interview screen calls.
    const effectiveOnBehalfOf =
      params !== undefined && 'onBehalfOfOverride' in params
        ? params.onBehalfOfOverride
        : onBehalfOf;

    dispatch(setLoading(true));
    dispatch(setLoadingProgress(0));

    const { onlyQitIds, answerGroupQits } = store.getState().interview;

    const apiParams: IPEParams = {
      surveyId: effectiveSurveyId,
      onBehalfOf: effectiveOnBehalfOf,
      deferred:
        params?.deferred ||
        (params?.skippedQitIds?.length
          ? { '-1': params.skippedQitIds }
          : undefined),
      onlyQitIds: params?.onlyQitIds ?? onlyQitIds ?? undefined,
      answerGroupQits: params?.answerGroupQits ?? answerGroupQits ?? undefined,
    };

    if (process.env.NODE_ENV !== 'production') {
      console.log(
        `[IPE] call  survey=${apiParams.surveyId}  ag_qits(p)=${_ag(
          params?.answerGroupQits
        )}  ag_qits(s)=${_ag(answerGroupQits)}  only(p)=${_a(
          params?.onlyQitIds
        )}  only(s)=${_a(onlyQitIds)}  skipped=${_a(params?.skippedQitIds)}`
      );
    }

    try {
      dispatch(setLoadingProgress(80));
      const data = await callIPEApi(apiParams);
      dispatch(setLoadingProgress(100));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(handleIPEResponse(data as any));
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { detail?: string; error?: string };
        };
      };
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.response?.data?.error;

      if (status === 400) {
        dispatch(setError('Something went wrong.'));
      } else if (status === 403) {
        dispatch(setError(detail || "You don't have access."));
      } else if (status === 404) {
        dispatch(setError('Interview not found.'));
      } else {
        // 5xx — retry once after a short delay
        try {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const retryData = await callIPEApi(apiParams);
          dispatch(setLoadingProgress(100));
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dispatch(handleIPEResponse(retryData as any));
        } catch {
          dispatch(setError("Couldn't load. Try again."));
        }
      }
    }
  };

  return { callIPE, loading };
}

export interface UseAnswerSubmitOptions {
  syncCurrentAnswerGroup?: boolean;
}

interface UseAnswerSubmitReturn {
  submitAnswer: (
    payload: AnswerPayload,
    options?: UseAnswerSubmitOptions
  ) => Promise<boolean>;
  answerInFlight: boolean;
  answerError: string | null;
}

export function useAnswerSubmit(): UseAnswerSubmitReturn {
  const dispatch = useDispatch();
  const { answerInFlight, answerError, onBehalfOf } = useSelector(
    (state: { interview: InterviewState }) => state.interview
  );

  const submitAnswer = async (
    payload: AnswerPayload,
    options?: UseAnswerSubmitOptions
  ): Promise<boolean> => {
    const syncCurrentAnswerGroup = options?.syncCurrentAnswerGroup ?? true;

    dispatch(setAnswerInFlight(true));
    dispatch(setAnswerError(null));

    try {
      const responseData = await submitAnswerApi(payload, onBehalfOf);
      dispatch(setAnswerInFlight(false));
      if (syncCurrentAnswerGroup) {
        const answerGroupId = (responseData as { answer_group_id?: number })
          ?.answer_group_id;
        if (answerGroupId) {
          dispatch(setAnswerGroupId(answerGroupId));
        }
      }
      return true;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { detail?: string } };
      };
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      const fail = (message: string): boolean => {
        dispatch(setAnswerError(message));
        dispatch(setAnswerInFlight(false));
        return false;
      };

      if (status === 400) {
        return fail(detail || 'Please check your answer.');
      }
      if (status === 401) {
        return fail('Your session expired. Please sign in again.');
      }
      if (status === 403) {
        return fail(detail || "You don't have access to save this answer.");
      }
      if (status === 404) {
        return fail('This questionnaire is no longer available.');
      }
      if (status && status < 500) {
        return fail(detail || "Couldn't save. Try again.");
      }

      // 5xx or network — retry once
      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const retryData = await submitAnswerApi(payload, onBehalfOf);
        dispatch(setAnswerInFlight(false));
        if (syncCurrentAnswerGroup) {
          const retryGroupId = (retryData as { answer_group_id?: number })
            ?.answer_group_id;
          if (retryGroupId) {
            dispatch(setAnswerGroupId(retryGroupId));
          }
        }
        return true;
      } catch {
        return fail("Couldn't save. Try again.");
      }
    }
  };

  return { submitAnswer, answerInFlight, answerError };
}

export function useValidateVisibility(): {
  validate: (qitIds: number[]) => Promise<Record<number, boolean>>;
} {
  const { surveyId, onBehalfOf } = useSelector(
    (state: { interview: InterviewState }) => state.interview
  );

  const validate = useCallback(
    (qitIds: number[]): Promise<Record<number, boolean>> => {
      if (surveyId == null) {
        return Promise.resolve({});
      }
      return validateVisibilityApi(surveyId, qitIds, onBehalfOf);
    },
    [surveyId, onBehalfOf]
  );

  return { validate };
}
